import { useEffect, useRef } from "react";
import { useZero } from "@rocicorp/zero/react";
import { useCurrentUser } from "../lib/current-user.js";
import { mutators } from "../lib/mutators.js";
import { AUTO_AWAY_TIMEOUT_MS } from "@/lib/presence";

const HEARTBEAT_INTERVAL_MS = 60_000; // Update last_active_at every 60s while active
const ACTIVITY_EVENTS = [
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
] as const;

/**
 * Tracks user activity and updates presence in Zero.
 * - Updates `last_active_at` on a throttled heartbeat while active
 * - Sets `is_online: false` after AUTO_AWAY_TIMEOUT_MS of inactivity
 * - Restores `is_online: true` on next activity
 * - Pauses heartbeat while the tab is hidden (CurrentUserProvider owns
 *   visibility-driven is_online toggling)
 *
 * Mount once in the app (e.g. inside AppInner).
 */
export function usePresenceTracking() {
  const zero = useZero();
  const { user } = useCurrentUser();
  const lastActivityRef = useRef(0);
  const isAwayRef = useRef(false);

  useEffect(() => {
    if (!user) return;

    // Reset refs when user changes (e.g. logout → login as different user)
    // so the new session starts fresh.
    lastActivityRef.current = Date.now();
    isAwayRef.current = false;

    const userId = user.id;

    function recordActivity() {
      lastActivityRef.current = Date.now();

      // If we were idle-away, come back online immediately
      if (isAwayRef.current) {
        isAwayRef.current = false;
        zero.mutate(mutators.user_presence.setOnline({ user_id: userId }));
      }
    }

    // Heartbeat: periodically flush last_active_at and check for auto-away.
    // Skip entirely when the tab is hidden — CurrentUserProvider already
    // sets is_online: false on visibilitychange, so we must not fight it.
    const heartbeat = setInterval(() => {
      if (document.visibilityState === "hidden") return;

      const now = Date.now();
      const idleDuration = now - lastActivityRef.current;

      if (idleDuration >= AUTO_AWAY_TIMEOUT_MS && !isAwayRef.current) {
        // Gone idle → mark away
        isAwayRef.current = true;
        zero.mutate(mutators.user_presence.setOffline({ user_id: userId }));
      } else if (!isAwayRef.current) {
        // Still active → heartbeat (updates last_active_at)
        zero.mutate(mutators.user_presence.heartbeat({ user_id: userId }));
      }
    }, HEARTBEAT_INTERVAL_MS);

    // When the tab becomes visible again after being hidden, reset the
    // away flag so the next user interaction can restore is_online.
    // (CurrentUserProvider already sets is_online: true on visible.)
    function handleVisibility() {
      if (document.visibilityState === "visible") {
        isAwayRef.current = false;
        lastActivityRef.current = Date.now();
      }
    }

    for (const event of ACTIVITY_EVENTS) {
      document.addEventListener(event, recordActivity, { passive: true });
    }
    document.addEventListener("visibilitychange", handleVisibility);

    return () => {
      clearInterval(heartbeat);
      for (const event of ACTIVITY_EVENTS) {
        document.removeEventListener(event, recordActivity);
      }
      document.removeEventListener("visibilitychange", handleVisibility);
    };
    // Re-subscribe only when the user identity itself changes — we don't want
    // to rebind every DOM listener on unrelated user-field updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, zero]);
}
